import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Alert, Badge, Image, Button, Form } from 'react-bootstrap';
import { IconExternalLink } from './Icons';
import { SimilarProduct } from '../store/slices/similarProductsSlice';
import { changeProductStatus, fetchStatuses } from '../store/slices/statusesSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import ImageTooltip from './ImageTooltip';

interface SimilarProductsModalProps {
  show: boolean;
  onHide: () => void;
  similarProducts: SimilarProduct[];
  loading: boolean;
  error: string | null;
  baseProductName: string;
  baseProductId: string;
}

const SimilarProductsModal: React.FC<SimilarProductsModalProps> = ({
  show,
  onHide,
  similarProducts,
  loading,
  error,
  baseProductName,
  baseProductId
}) => {
  const dispatch = useAppDispatch();
  const { statuses, loading: statusesLoading } = useAppSelector((state) => state.statuses);
  const [changingStatus, setChangingStatus] = useState<string | null>(null);

  // Загружаем статусы при открытии модального окна
  useEffect(() => {
    if (show && statuses.length === 0) {
      dispatch(fetchStatuses());
    }
  }, [show, dispatch, statuses.length]);
  
  const [imageTooltip, setImageTooltip] = useState<{
    show: boolean;
    imageUrl: string;
    alt: string;
    position: { x: number; y: number };
  }>({
    show: false,
    imageUrl: '',
    alt: '',
    position: { x: 0, y: 0 }
  });
  const formatCurrency = (amount: number) => {
    if (!amount) return '0 ₽';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatSimilarityScore = (score: number) => {
    return `${(score * 100).toFixed(1)}%`;
  };

  const getSimilarityBadgeColor = (score: number) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    if (score >= 0.4) return 'info';
    return 'secondary';
  };

  // Функция для получения цвета статуса
  const getStatusColor = (statusName: string) => {
    const colorMap: { [key: string]: string } = {
      'В процессе': 'status-white',
      'Расчет ЮЭ': 'status-green',
      'Уже продаем': 'status-gray',
      'Слабый потенциал': 'status-red',
      'Не соответствует ассортиментной матрице': 'status-purple',
      'Сезонность': 'status-lightblue',
      'Доп вариация': 'status-teal',
      'Требуется дополнительная консультация': 'status-yellow',
      'Электротовары': 'status-red'
    };
    return colorMap[statusName] || 'status-white';
  };

  const handleProductClick = (productUrl: string) => {
    window.open(productUrl, '_blank', 'noopener,noreferrer');
  };

  const handleStatusChange = async (productId: string, statusId: string) => {
    setChangingStatus(productId);
    try {
      await dispatch(changeProductStatus({ productId, statusId })).unwrap();
    } catch (error) {
      console.error('Failed to change product status:', error);
    } finally {
      setChangingStatus(null);
    }
  };

  // Обработчики для увеличенного изображения
  const handleImageMouseEnter = (imageUrl: string, alt: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setImageTooltip({
      show: true,
      imageUrl,
      alt,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    });
  };

  const handleImageMouseLeave = () => {
    setImageTooltip({
      show: false,
      imageUrl: '',
      alt: '',
      position: { x: 0, y: 0 }
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Похожие товары
          <Badge bg="primary" className="ms-2">
            {similarProducts.length}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Базовый товар:</strong> {baseProductName} (ID: {baseProductId})
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Загрузка похожих товаров...</span>
            </Spinner>
            <div className="mt-2">Поиск похожих товаров...</div>
          </div>
        ) : error ? (
          <Alert variant="danger">
            <strong>Ошибка:</strong> {error}
          </Alert>
        ) : similarProducts.length === 0 ? (
          <Alert variant="info">
            Похожие товары не найдены.
          </Alert>
        ) : (
           <div className="table-responsive">
             <Table hover variant="dark" className="table-dark" size="sm">
              <thead>
                 <tr>
                   <th>img</th>
                   <th>Название</th>
                   <th>Статус</th>
                   <th>Схожесть</th>
                   <th>Предмет</th>
                   <th>Цена</th>
                   <th>Выручка</th>
                   <th>Продажи</th>
                   <th>Страна</th>
                   <th>Продавец</th>
                   <th>Действия</th>
                 </tr>
              </thead>
              <tbody>
                {similarProducts.map((product) => (
                  <tr key={product.product_id}>
                     <td>
                       {product.product_thumb_url ? (
                         <Image 
                           src={product.product_thumb_url} 
                           alt={product.product_name}
                           width={40}
                           height={40}
                           className="rounded product-image-hover"
                           style={{ objectFit: 'cover', cursor: 'pointer' }}
                           onMouseEnter={(e) => handleImageMouseEnter(product.product_thumb_url!, product.product_name, e)}
                           onMouseLeave={handleImageMouseLeave}
                         />
                       ) : (
                         <div className="bg-light rounded d-flex align-items-center justify-content-center" 
                              style={{ width: 40, height: 40 }}>
                           <IconExternalLink className="text-muted" size={16} />
                         </div>
                       )}
                     </td>
                    <td>
                      <div className="fw-bold" style={{ maxWidth: '200px' }}>
                        {product.product_name}
                      </div>
                      <small className="text-muted">ID: {product.product_id}</small>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Select
                          size="sm"
                          value={product.status_id || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(product.product_id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={statusesLoading || changingStatus === product.product_id}
                          className={product.status_name ? getStatusColor(product.status_name) : 'status-white'}
                          style={{ minWidth: '150px' }}
                        >
                          {statuses.map(status => (
                            <option 
                              key={status.id} 
                              value={status.id}
                              className={getStatusColor(status.name)}
                            >
                              {status.name}
                            </option>
                          ))}
                        </Form.Select>
                        {changingStatus === product.product_id && (
                          <Spinner size="sm" animation="border" variant="primary" />
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg={getSimilarityBadgeColor(product.similarity_score)}>
                        {formatSimilarityScore(product.similarity_score)}
                      </Badge>
                    </td>
                    <td>
                      <div style={{ maxWidth: '150px' }}>
                        {product.subject_name.split('/').pop() || product.subject_name}
                      </div>
                    </td>
                    <td>
                      <div className="fw-bold">{formatCurrency(product.price_final_average)}</div>
                      <small className="text-muted">Медиана: {formatCurrency(product.price_final_median)}</small>
                    </td>
                    <td>
                      <div className="fw-bold text-success">{formatCurrency(product.stat_revenue)}</div>
                      <small className="text-muted">Потенциал: {formatCurrency(product.stat_revenue_potential)}</small>
                    </td>
                     <td>
                       <div className="fw-bold">{product.stat_sales || 0}</div>
                       {product.stat_purchase_percent && (
                         <small className="text-muted">
                           {product.stat_purchase_percent}% покупок
                         </small>
                       )}
                     </td>
                    <td>{product.meta_country || '-'}</td>
                    <td>
                      <div style={{ maxWidth: '120px' }}>
                        {product.seller_name || '-'}
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleProductClick(product.product_url)}
                        title="Открыть товар"
                      >
                        <IconExternalLink size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
       <Modal.Footer>
         <Button variant="secondary" onClick={onHide}>
           Закрыть
         </Button>
       </Modal.Footer>

       {/* Image Tooltip */}
       <ImageTooltip 
         imageUrl={imageTooltip.imageUrl}
         alt={imageTooltip.alt}
         show={imageTooltip.show}
         position={imageTooltip.position}
       />
     </Modal>
   );
 };
 
 export default SimilarProductsModal;
